import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { durationToText } from 'modules/core/lib/duration'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import DayInput from 'modules/shift_management/components/Settings/ShiftTemplates/Content/EditTemplateWeek/WeekSettings/WeekConfiguration/DayInput'
import { ShiftConfigWithBreaksForm } from 'modules/shift_management/components/Settings/ShiftTemplates/Content/EditTemplateWeek/WeekSettings/WeekConfiguration/forms'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import type ShiftConfigForm from 'modules/shift_management/lib/forms/ShiftConfigForm'
import { totalDurationFromUnpaidBreakForms } from 'modules/shift_management/lib/totalDurationFromUnpaidBreaks'
import { shiftDaysSortedByStartingWeekday } from 'modules/shift_management/store/api/models/ShiftConfiguration'

import InformationTooltip from './InformationTooltip'

interface Props {
  running: boolean
  onConfirm: () => void
  onAddShift: (day: number) => void
  onDeleteShift: (form: ShiftConfigForm) => void
  onCopyFirst: (forms: ShiftConfigWithBreaksForm[]) => void
  onAddBreak: (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    shiftConfig: ShiftConfigWithBreaksForm,
    isTimeFormat24H: boolean
  ) => void
  onDeleteBreak: (dayConfig: ShiftConfigWithBreaksForm, form: PlannedBreakForm) => void
  onAddRestDay: (weekday: number) => void
  onAddEmptyCell: (weekday: number) => void
  shiftConfigForms: ShiftConfigWithBreaksForm[]
}

const ShiftFormComponent = ({
  running,
  onConfirm,
  onAddShift,
  onDeleteShift,
  onAddBreak,
  onDeleteBreak,
  onAddRestDay,
  onAddEmptyCell,
  onCopyFirst,
  shiftConfigForms,
}: Props) => {
  // Returns a hash with the following shape: { 0: 'Mon', 1: 'Tue', ... }
  // We're doing this at runtime so that the string values are localized by momentjs
  const WEEKDAY_SHORT_NAMES_BY_INDEX = moment
    .weekdaysShort()
    .slice(1)
    .concat(moment.weekdaysShort()[0] as string)
    .reduce((memo: Record<string, string>, name: string, index: number) => {
      memo[index] = name
      return memo
    }, {})
  const WEEKDAY_LONG_NAMES_BY_INDEX = moment
    .weekdays()
    .slice(1)
    .concat(moment.weekdays()[0] as string)
    .reduce((memo: Record<string, string>, name: string, index: number) => {
      memo[index] = name
      return memo
    }, {})

  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const { isTimeFormat24H } = useIsTimeFormat24H()

  // eg: If user chooses to start the week on Wed, then it returns [2, 3, 4, 5, 6, 0, 1]
  const weekdaysStartingByUserFirstDayOfWeek = shiftDaysSortedByStartingWeekday(firstDayOfWeek)

  const title = i18n.t('shift_management.templates.weeks.settings_hours')
  const total = i18n.t('shift_management.templates.weeks.settings_total')

  const restDayFlag = hasFeature('shift_management_rest_day')

  const filteredShifts = (index: number) => {
    return shiftConfigForms.filter((shift) => +shift.shiftConfig.weekday === index)
  }

  const totalDurationShifts = shiftConfigForms.reduce(
    (acc, { shiftConfig: { isValid, duration }, breaks }) => {
      const shiftDuration = isValid
        ? duration(isTimeFormat24H) - totalDurationFromUnpaidBreakForms(breaks)
        : 0
      return acc + shiftDuration
    },
    0
  )

  const totalDurationText = durationToText(totalDurationShifts / 60, 'minutes')

  const isShiftFormValid =
    totalDurationShifts >= 0 &&
    shiftConfigForms.every(({ shiftConfig, breaks }) => {
      const { isValid, formattedStartAt: startAt, formattedEndAt: endAt } = shiftConfig
      return isValid && breaks.every((form) => form.isValid({ startAt, endAt }))
    })

  const onClickCopy = () => {
    const dayIndex = weekdaysStartingByUserFirstDayOfWeek[0]
    // If no day index is defined, then we use 0 as default for Monday
    // as also defined in the ShiftConfiguration model
    const firstDayShiftConfigs = filteredShifts(dayIndex || 0)
    return onCopyFirst(firstDayShiftConfigs)
  }

  return (
    <>
      <Box gap="s8">
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
          <Box flexDirection="row" gap="s4">
            <Text.Normal color="grey800" size="200" weight="medium">
              {title}
            </Text.Normal>
            {restDayFlag && <InformationTooltip />}
          </Box>
          {restDayFlag && (
            <Button onClick={() => onClickCopy()} type="secondary" iconName="layers-front">
              {i18n.t('shift_management.templates.weeks.settings_copy')}
            </Button>
          )}
        </Box>
        {weekdaysStartingByUserFirstDayOfWeek.map((dayIndex) => (
          <DayInput
            key={dayIndex}
            dayShort={WEEKDAY_SHORT_NAMES_BY_INDEX[dayIndex] as string}
            dayLong={WEEKDAY_LONG_NAMES_BY_INDEX[dayIndex] as string}
            weekIndex={dayIndex}
            shiftsForms={filteredShifts(dayIndex)}
            onAddShift={onAddShift}
            onDeleteShift={onDeleteShift}
            onDeleteBreak={onDeleteBreak}
            onAddBreak={onAddBreak}
            onAddRestDay={onAddRestDay}
            onAddEmptyCell={onAddEmptyCell}
          />
        ))}
      </Box>
      <Box
        flexDirection="row"
        gap="s8"
        justifyContent="spaceBetween"
        padding="s24"
        background="grey000White"
        borderRadius={{
          all: 'abs008',
        }}
      >
        <Text.Normal color="grey1000" size="200" weight="medium">
          {total}
        </Text.Normal>
        <Text.Normal color="grey1000" size="200" weight="medium">
          {totalDurationText}
        </Text.Normal>
      </Box>
      <Box alignItems="flexEnd">
        <Button disabled={running || !isShiftFormValid} onClick={onConfirm}>
          {i18n.t('common.submit')}
        </Button>
      </Box>
    </>
  )
}

export default observer(ShiftFormComponent)
